import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config/api';
import { FaArrowRight, FaImage } from 'react-icons/fa';
import './AdminMeetMeEdit.css';

const defaultForm = {
  subtitle_text: 'من أنا؟',
  title_text: 'رسالتي هي اختصار الطريق عليكِ، وحمايتك من الأخطاء المنهجية.',
  button_text: 'اقرأ قصتي الكاملة',
};

const AdminMeetMeEdit = () => {
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultForm);

  // Existing URLs from DB (for preview)
  const [imageUrls, setImageUrls] = useState({
    image1_url: '',
    image2_url: '',
    image3_url: '',
  });

  const [files, setFiles] = useState({
    image1: null,
    image2: null,
    image3: null,
  });

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/admin/meetme`, { headers: getAuthHeaders() })
      .then((res) => {
        const m = res.data?.meetme;
        if (!m) return;
        setForm({
          subtitle_text: m.subtitle_text ?? defaultForm.subtitle_text,
          title_text: m.title_text ?? defaultForm.title_text,
          button_text: m.button_text ?? defaultForm.button_text,
        });
        setImageUrls({
          image1_url: m.image1_url ?? '',
          image2_url: m.image2_url ?? '',
          image3_url: m.image3_url ?? '',
        });
      })
      .catch((err) => {
        console.error('Failed to load meetme:', err);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFile = (field, file) => {
    setFiles((prev) => ({ ...prev, [field]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('subtitle_text', form.subtitle_text);
      fd.append('title_text', form.title_text);
      fd.append('button_text', form.button_text);

      if (files.image1) fd.append('image1', files.image1);
      if (files.image2) fd.append('image2', files.image2);
      if (files.image3) fd.append('image3', files.image3);

      await axios.put(`${API_BASE}/api/admin/meetme`, fd, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('تم حفظ قسم "من أنا؟" بنجاح');
      navigate('/admin');
    } catch (err) {
      alert(err.response?.data?.error || 'فشل حفظ القسم');
    } finally {
      setSaving(false);
    }
  };

  const PreviewImage = ({ url, label }) => {
    const toAbsoluteImageUrl = (maybeUrl) => {
      if (!maybeUrl) return '';
      const u = String(maybeUrl).trim();
      if (!u) return '';
      if (u.startsWith('http://') || u.startsWith('https://')) return u;
      return `${API_BASE}${u}`;
    };

    if (!url) return <div className="admin-meetme-preview-empty">{label}</div>;
    return (
      <div className="admin-meetme-preview">
        <img src={toAbsoluteImageUrl(url)} alt={label} />
      </div>
    );
  };

  if (loading) return <div className="admin-loading">جاري التحميل...</div>;

  return (
    <div className="admin-meetme-edit" dir="rtl">
      <div className="admin-meetme-edit-header">
        <Link to="/admin" className="back-link">
          <FaArrowRight /> العودة للصفحة الرئيسية
        </Link>
        <h1 className="admin-page-title">تعديل قسم "من أنا؟"</h1>
      </div>

      <form onSubmit={handleSubmit} className="admin-meetme-form">
        <div className="form-row">
          <label>العنوان (Subtitle)</label>
          <input
            type="text"
            value={form.subtitle_text}
            onChange={(e) => handleChange('subtitle_text', e.target.value)}
            className="form-input"
            required
          />
        </div>

        <div className="form-row">
          <label>النص الرئيسي (Title)</label>
          <textarea
            value={form.title_text}
            onChange={(e) => handleChange('title_text', e.target.value)}
            className="form-input"
            rows={3}
            required
          />
        </div>

        <div className="form-row">
          <label>نص زر الرابط</label>
          <input
            type="text"
            value={form.button_text}
            onChange={(e) => handleChange('button_text', e.target.value)}
            className="form-input"
            required
          />
        </div>

        <div className="admin-meetme-images-grid">
          <div className="admin-meetme-image-block">
            <div className="admin-meetme-image-label">الصورة 1</div>
            <PreviewImage url={imageUrls.image1_url} label="بدون صورة" />
            <div className="admin-meetme-upload">
              <FaImage />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFile('image1', e.target.files?.[0] || null)}
              />
            </div>
          </div>

          <div className="admin-meetme-image-block">
            <div className="admin-meetme-image-label">الصورة 2</div>
            <PreviewImage url={imageUrls.image2_url} label="بدون صورة" />
            <div className="admin-meetme-upload">
              <FaImage />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFile('image2', e.target.files?.[0] || null)}
              />
            </div>
          </div>

          <div className="admin-meetme-image-block">
            <div className="admin-meetme-image-label">الصورة 3</div>
            <PreviewImage url={imageUrls.image3_url} label="بدون صورة" />
            <div className="admin-meetme-upload">
              <FaImage />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFile('image3', e.target.files?.[0] || null)}
              />
            </div>
          </div>
        </div>

        <div className="admin-meetme-actions">
          <button type="submit" className="admin-btn-primary" disabled={saving}>
            {saving ? 'جاري الحفظ...' : 'حفظ'}
          </button>
          <button
            type="button"
            className="admin-btn-secondary"
            onClick={() => navigate('/admin')}
            disabled={saving}
          >
            إلغاء
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminMeetMeEdit;

